import { CurrencyMinor } from '@fox/atoms';
import { PaymentMethod, ProviderSelection, SetupIntentResult } from '@fox/finance';
import { Plan, validateDiscountCode } from '@fox/forms-runtime';
import Sqids from 'sqids';
import {
	CompleteIntentInputModelBase,
	DecodedSwiftFoxPlansResult,
	DirectDebitModel,
	FormLineItem,
	SwiftFoxFormCreateDebitInputModel,
	SwiftFoxFormSetupIntentModel,
	UpdateIntentInputModel
} from '../../Membership/financial-details/contracts';
import {
	convertMemberPortalIntervalToSfEnumValue,
	convertMemberPortalPlanOptionTypeToSfEnumValue,
	convertMemberPortalPlanTypeToSfEnumValue
} from '../../Membership/financial-details/shared';
import { DiscountResult, TaxRate } from './contracts';
import { addContentId, mutateHelper, queryHelper } from './fetch-templates';

export async function fetchPlans(contentId: string, requestToken: string, ids?: string | null): Promise<Plan[]> {
	let url = `/Umbraco/Api/Membership/DecodedPlans?${addContentId(contentId)}`;

	const sqids = new Sqids();

	const decoded = ids?.length ? sqids.decode(ids) : null;
	if (decoded?.length) {
		url += decoded.map(id => `&ids=${encodeURIComponent(id)}`).join('');
	}
	const res = await queryHelper<DecodedSwiftFoxPlansResult[]>(requestToken, url);
	if (res.error) {
		throw new Error(res.error);
	}

	// Decode all ids for each item
	const plans: Plan[] =
		res.data?.map(plan => {
			return {
				...plan,
				type: convertMemberPortalPlanTypeToSfEnumValue(plan.type),
				fees:
					plan.fees?.map(fee => {
						const interval = convertMemberPortalIntervalToSfEnumValue(fee.interval);
						return {
							...fee,
							effectiveFrom: fee.effectiveFrom?.toString(),
							interval
						};
					}) ?? [],
				options:
					plan.options?.map(option => {
						return {
							...option,
							interval: convertMemberPortalIntervalToSfEnumValue(option.interval),
							type: convertMemberPortalPlanOptionTypeToSfEnumValue(option.type)
						};
					}) ?? []
			};
		}) ?? [];

	return plans;
}
export async function fetchTaxRates(contentId: string, requestToken: string) {
	const url = `/Umbraco/Api/Membership/TaxRates?${addContentId(contentId)}`;
	const res = await queryHelper<TaxRate[]>(requestToken, url);
	if (res.error) {
		throw new Error(res.error);
	}
	return res.data ?? [];
}
export async function fetchAccountInfo(
	contentId: string,
	requestToken: string,
	ownerId?: string,
	ownerType?: string
): Promise<{ error?: string; data?: FormLineItem[] }> {
	const url = `/Umbraco/Api/Membership/SubscriptionOutstanding?${addContentId(contentId)}&ownerId=${ownerId}&ownerType=${ownerType}`;
	const res = await queryHelper<FormLineItem[]>(requestToken, url);

	return { data: res.data ?? [], error: res.error };
}

export async function completeCardIntent(
	contentId: string,
	requestToken: string,
	intentId: string,
	data: string
): Promise<{ error?: string; id?: string }> {
	const url = '/Umbraco/Api/Membership/FormCompleteCardIntent';
	const res = await mutateHelper<string, CompleteIntentInputModelBase>({ contentId, data, id: intentId }, requestToken, url);
	if (res.error) {
		return { error: res.error };
	}
	return { id: res.data };
}
export async function createDirectDebit(input: {
	contentId: string;
	requestToken: string;
	ownerType: string;
	ownerId: string;
	bsb;
	account: string;
	accountName: string;
	sourceId: string;
	provider: string;
}): Promise<{ error?: string; id?: string }> {
	const { contentId, requestToken, ownerType, ownerId, bsb, account, accountName, sourceId, provider } = input;
	const url = '/Umbraco/Api/Membership/FormCreateDirectDebit';
	const res = await mutateHelper<DirectDebitModel, SwiftFoxFormCreateDebitInputModel>(
		{ contentId, ownerType, ownerId, bsb, account, accountName, sourceId, provider },
		requestToken,
		url
	);
	if (res.error) {
		return { error: res.error };
	}
	return { id: res.data.id };
}
export async function completeDebitIntent(
	contentId: string,
	requestToken: string,
	intentId: string,
	data: string
): Promise<{ error?: string; id?: string }> {
	const url = '/Umbraco/Api/Membership/FormCompleteDebitIntent';
	const res = await mutateHelper<string, CompleteIntentInputModelBase>({ contentId, data, id: intentId }, requestToken, url);
	if (res.error) {
		return { error: res.error };
	}
	return { id: res.data };
}
export async function prorateFee(contentId: string, requestToken: string, feeId: number): Promise<CurrencyMinor> {
	const url = `/Umbraco/Api/Membership/ProrateFee?${addContentId(contentId)}&feeId=${feeId}`;
	const res = await queryHelper<CurrencyMinor>(requestToken, url);
	if (res.error) {
		throw new Error('An unexpected error occured.');
	}
	return res.data;
}
export async function setupIntent(input: {
	contentId: string;
	requestToken: string;
	provider: ProviderSelection;
	name: string;
	email: string;
	ownerType?: string;
	ownerId?: string;
}): Promise<SetupIntentResult | undefined> {
	const { contentId, requestToken, provider, name, email, ownerType, ownerId } = input;
	const url = '/Umbraco/Api/Membership/FormSetupIntent';
	const res = await mutateHelper<SetupIntentResult, SwiftFoxFormSetupIntentModel>(
		{ contentId, method: provider.method, provider: provider.provider, name, ownerId, ownerType, email },
		requestToken,
		url
	);
	return res.data ?? undefined;
}
export async function updateIntent(
	contentId: string,
	requestToken: string,
	amount: number,
	intentId: string,
	method: PaymentMethod,
	provider: string
) {
	const url = '/Umbraco/Api/Membership/FormUpdateIntent';
	const res = await mutateHelper<SetupIntentResult, UpdateIntentInputModel>(
		{ contentId, method, provider, amount, intentId },
		requestToken,
		url
	);
	return res.data ?? undefined;
}
export async function validateDiscount(
	contentId: string,
	requestToken: string,
	discountToApply: string,
	subscriptionSelections: string[],
	ownerId?: string
) {
	const codeInput = discountToApply.trim();
	const url = `/Umbraco/Api/Membership/DiscountsByCode?${addContentId(contentId)}&code=${encodeURIComponent(codeInput)}`;
	const res = await queryHelper<DiscountResult>(requestToken, url);
	if (res.error) {
		return { error: res.error };
	}

	const code = res.data?.codes?.find(c => c.code?.toLowerCase() === codeInput?.toLowerCase());
	const [isValid, reason] = validateDiscountCode(res.data, code, subscriptionSelections, ownerId);

	if (!isValid) return { error: reason };

	return {
		data: {
			amount: res.data.amount,
			appliesTo: res.data.appliesTo,
			code: code?.code,
			codeId: code?.id,
			description: res.data.description,
			discountId: res.data.id,
			discountType: res.data.discountType,
			ownerId: res.data.ownerId,
			ownerType: res.data.ownerType
		}
	};
}
